/**
 * INTAKE-V2-BUILD-V1 -- the arrival registration wizard's PURE model
 * (authority: docs/design/intake-registration-v2/DESIGN-BRIEF.md, merged
 * #410; founder rulings D2-D7 2026-07-31).
 *
 * Everything interactive about the wizard that can be a pure function IS one
 * (the repo test idiom: renderToStaticMarkup + exported pure helpers -- no
 * jsdom, so state transitions and validation pin HERE):
 *
 *   - the step machine: SEARCH-FIRST entry (C13), then the path resolves
 *     after the search step and NEVER changes mid-flow (brief 6.2) --
 *     NEW = 5 steps, RETURNING = 4 steps, the minor stays at 5;
 *   - the requiredness model (brief 6.4, three renderings): resolved from
 *     org config (registration.required_fields + gender_required composed
 *     server-side; must-match 7 -- zero tenant field lists in code);
 *   - founder ruling D3: per-step validation on Continue -- the error
 *     renders BESIDE the exact field, focus moves to it, nothing typed is
 *     ever lost; NO whole-form locking, NO per-keystroke server validation;
 *   - founder ruling D5 (V1-SIMPLE) as corrected by the PR #414 founder
 *     review (DRAFT PRIVACY): the draft lives on the device where entry
 *     happens but its lifetime is SESSION-BOUND and ACTOR-BOUND --
 *     versioned, appointment-keyed, carrying the authenticated staff
 *     actor; the HOST stores it in session storage only, and parse
 *     discards any draft whose actor is not the CURRENT authenticated
 *     user (stale key = dead draft). It feeds the ONE existing submit and
 *     is NEVER a second writer -- serialize / parse are pure and strict
 *     (a malformed draft is discarded whole, never half-restored);
 *   - the display-language inventory (brief 12.3 + the polished mockups,
 *     word discipline carried verbatim; ASCII-clean by the operating law).
 *
 * The submit body builder mirrors the EXISTING checkpoint client body
 * byte-for-byte in shape (CheckinIdentityStep.tsx submitCheckpoint) -- the
 * wizard recomposes presentation over the SAME wire; the duplicate-first
 * ladder, the candidacy fence, and the mint path stay the server's
 * (api/routes/frontdesk-registration-checkpoint.ts, #396/#402 machinery).
 *
 * CONSENT PATHS (C14, founder item 6): 'digital' = signed on screen -- the
 * registration/privacy consent rides the submit exactly as today. 'paper' =
 * printed and signed -- NOT RECORDED until photographed: the submit carries
 * NO registrationPrivacyConsent and the record shows the loud PENDING PHOTO
 * state with exactly one action (consent-photo.tsx). A MINOR's guardian
 * consent is the existing writer's hard rule (no minor create without it)
 * and rides the submit on BOTH paths -- the physical/on-screen guardian act
 * happens at the desk either way; only the registration-privacy ARTIFACT
 * waits for its photo.
 */

import {
  isPlausibleEmail,
  isPlausibleMobile,
} from '@/dashboard/patients/patient-master-view';
import { parseDdMmYyyyToIso } from '@/lib/datetime-ist';

// ---- Config (the GET /api/registration-config wire) ------------------------

export type RegistrationRequiredField =
  | 'full_name'
  | 'mobile'
  | 'age_or_dob'
  | 'gender'
  | 'city'
  | 'reason_for_visit'
  // RECEPTION-OS-BUILD-V1 (Q-12=A): requirable registration fields; they
  // render ONLY when the org's required_fields row names them (their
  // columns are FILE-ONLY migration 20260803_1, config seeded post-apply).
  | 'relation_name'
  | 'occupation'
  // AUG-12-WALK-BLOCKERS-V1 (D2): the Q-09=D handling TAG is requirable
  // (mirror of the backend vocabulary; select renders only when the
  // sibling handling_categories vocabulary is non-empty).
  | 'handling_category'
  // REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE full parity): the
  // remaining Partha contract tokens, aligning this client union with the
  // canonical server vocabulary (REGISTRATION_REQUIRED_FIELD_TOKENS) so BOTH
  // entry points config-gate the SAME governed fields through the ONE module.
  | 'name_title'
  | 'last_name'
  | 'relation_title'
  | 'address_area'
  | 'state'
  | 'pincode'
  | 'address_door'
  | 'address_street'
  | 'address_landmark'
  | 'address_taluka'
  | 'address_country'
  | 'alt_mobile'
  | 'alt_mobile_relation'
  | 'alt_mobile_relation_name'
  | 'emergency_contact_name'
  | 'emergency_contact_relation'
  | 'emergency_contact_phone';

export interface RegistrationReasonChip {
  readonly code: string;
  readonly label: string;
}

export type RegistrationConsentPath = 'digital' | 'paper';

/** Mirror of the backend RegistrationConsentFormTemplate (zero PHI). */
export interface RegistrationConsentFormTemplate {
  readonly title: string;
  readonly bodyLines: readonly string[];
  readonly imageUrl: string | null;
}

export interface RegistrationConfigView {
  readonly requiredFields: readonly RegistrationRequiredField[];
  readonly genderRequired: boolean;
  readonly reasonChips: readonly RegistrationReasonChip[];
  readonly consentPaths: readonly RegistrationConsentPath[];
  /** Q-09=D handling-category TAG vocabulary; [] = the select renders
   *  nowhere (also keeps an unapplied 20260803_1 column unreachable). */
  readonly handlingCategories: readonly string[];
  /** REGISTRATION-FIELD-CONTROLS-V1: the governed picklist vocabularies +
   *  address defaults the server view has always resolved (lib/domain/
   *  registration/registration-config.ts RegistrationConfigView) but this
   *  client mirror under-declared -- so the wizard doors rendered the
   *  Title / Relation / Occupation controls as FREE TEXT while the desk
   *  modal (fed by the intake prefill wire) rendered governed picklists,
   *  and a free-typed relation then 400d at the writers. All optional +
   *  tolerant: an older server or the fallback degrades each control to
   *  free text (R-017) exactly as before. */
  readonly occupationOptions?: readonly string[];
  readonly nameTitleOptions?: readonly string[];
  readonly relationTitleOptions?: readonly string[];
  readonly relationOptions?: readonly string[];
  readonly stateDistricts?: Readonly<Record<string, readonly string[]>>;
  readonly stateDefault?: string;
  readonly countryDefault?: string;
  /** The consent-form print template; null = the honest SOURCED-PENDING
   *  placeholder (legal text is never invented). */
  readonly consentFormTemplate: RegistrationConsentFormTemplate | null;
}

/** The vocabulary bundle the shared registration module draws its governed
 *  picklists from (structurally = RegistrationSectionsPrefill; declared
 *  here without importing the component type to keep the model free of a
 *  module cycle). ONE derivation for every wizard host -- never a second
 *  hand-built copy per door. */
export interface RegistrationVocabularyPrefill {
  readonly nameTitleOptions?: readonly string[];
  readonly relationOptions?: readonly string[];
  readonly relationTitleOptions?: readonly string[];
  readonly occupationOptions?: readonly string[];
  readonly handlingCategories?: readonly string[];
  readonly stateDistricts?: Readonly<Record<string, readonly string[]>>;
}

/** REGISTRATION-FIELD-CONTROLS-V1: the ONE config -> module-prefill
 *  derivation all three wizard hosts (check-in, person-only page, walk-in
 *  register modal) pass to the shared RegistrationContractSections, so
 *  every registration door shows the SAME governed choices for the same
 *  org config. Absent fields degrade the matching control to free text
 *  (R-017) -- a config fault never blocks a registration. */
export function registrationSectionsPrefill(
  config: RegistrationConfigView,
): RegistrationVocabularyPrefill {
  return {
    nameTitleOptions: config.nameTitleOptions ?? [],
    relationOptions: config.relationOptions ?? [],
    relationTitleOptions: config.relationTitleOptions ?? [],
    occupationOptions: config.occupationOptions ?? [],
    handlingCategories: config.handlingCategories,
    stateDistricts: config.stateDistricts ?? {},
  };
}

/**
 * The client-side fallback when the config READ itself fails (network /
 * denied) -- the LAW trio only, both consent paths, no chips. The safety
 * direction is D3's: an unresolvable read must NEVER block a registration,
 * so the fallback requires nothing beyond what the server enforces anyway.
 * (The server read degrades to the SEEDED defaults internally and never
 * throws; this fallback is for the wire failing outright.)
 */
export const FALLBACK_REGISTRATION_CONFIG: RegistrationConfigView = {
  requiredFields: ['full_name', 'mobile', 'age_or_dob'],
  genderRequired: false,
  reasonChips: [],
  consentPaths: ['digital', 'paper'],
  handlingCategories: [],
  consentFormTemplate: null,
};

/** The ONE resolved requiredness set the three renderings read (brief 6.4):
 *  requiredFields UNION (genderRequired ? gender : nothing). */
export function resolvedRequiredFields(
  config: RegistrationConfigView,
): ReadonlySet<RegistrationRequiredField> {
  const out = new Set<RegistrationRequiredField>(config.requiredFields);
  if (config.genderRequired) out.add('gender');
  return out;
}

/** Frozen arrival adds the formerly lead-only facts to the existing Patient
 * Master writer through 20261010_4. Keep the compatibility export; no configured
 * field is now silently removed from the form's required set. */
export const PATIENT_PLANE_UNSTORED_TOKENS: ReadonlySet<RegistrationRequiredField> =
  new Set<RegistrationRequiredField>();

/** Narrow an org required set to what the person-only patients-plane writer can
 *  actually persist (drops PATIENT_PLANE_UNSTORED_TOKENS). Used by the person-
 *  only hosts for BOTH the module render (isRequired) AND the submit gate
 *  (missingRequiredCreateKeys), so rendered-required == gated == persisted. */
export function patientPlaneRequiredFields(
  required: ReadonlySet<RegistrationRequiredField>,
): ReadonlySet<RegistrationRequiredField> {
  const out = new Set<RegistrationRequiredField>();
  for (const k of required) {
    if (!PATIENT_PLANE_UNSTORED_TOKENS.has(k)) out.add(k);
  }
  return out;
}

// ---- The step machine ------------------------------------------------------

/** The path resolves after the search step and the length is then FIXED and
 *  shown -- it never changes underneath the operator (brief 6.2). */
export type ArrivalPath = 'new' | 'returning';

export type ArrivalStepId = 'search' | 'details' | 'reason' | 'consent' | 'review';

/** NEW path: 5 steps. RETURNING path: 4 steps -- consent already on file is
 *  a fact shown on review, never a step. The MINOR stays at 5: the guardian
 *  block joins the consent step rather than adding one (brief 6.2). */
export function arrivalSteps(path: ArrivalPath): readonly ArrivalStepId[] {
  return path === 'returning'
    ? ['search', 'details', 'reason', 'review']
    : ['search', 'details', 'reason', 'consent', 'review'];
}

export function arrivalStepCount(path: ArrivalPath): number {
  return arrivalSteps(path).length;
}

/** 1-based position of a step on the given path ('Step N of M'). */
export function arrivalStepNumber(step: ArrivalStepId, path: ArrivalPath): number {
  return arrivalSteps(path).indexOf(step) + 1;
}

export function nextArrivalStep(
  step: ArrivalStepId,
  path: ArrivalPath,
): ArrivalStepId | null {
  const steps = arrivalSteps(path);
  const i = steps.indexOf(step);
  return i >= 0 && i + 1 < steps.length ? (steps[i + 1] as ArrivalStepId) : null;
}

export function previousArrivalStep(
  step: ArrivalStepId,
  path: ArrivalPath,
): ArrivalStepId | null {
  const steps = arrivalSteps(path);
  const i = steps.indexOf(step);
  return i > 0 ? (steps[i - 1] as ArrivalStepId) : null;
}

/** The wizard title per step (the mockup header vocabulary). */
export function arrivalStepTitle(step: ArrivalStepId, path: ArrivalPath): string {
  if (step === 'search') return ARRIVAL_TITLE_SEARCH;
  if (step === 'review') return ARRIVAL_TITLE_REVIEW;
  return path === 'returning' ? ARRIVAL_TITLE_RETURNING : ARRIVAL_TITLE_NEW;
}

// ---- Form state ------------------------------------------------------------

export interface ArrivalWizardForm {
  contactReuse?: string;
  readonly fullName: string;
  readonly mobile: string;
  readonly ageMode: 'age' | 'dob';
  readonly ageYears: string;
  readonly dob: string;
  /** '' = NOT ANSWERED (F8 fix: nothing is pre-answered for the operator;
   *  the wire maps '' to the stored 'unknown' only at submit). */
  readonly gender: string;
  readonly city: string;
  readonly complaint: string;
  readonly alertFlag: boolean;
  /** '' = not chosen yet; the consent step's one question. */
  readonly consentPath: '' | RegistrationConsentPath;
  readonly guardianName: string;
  readonly guardianRelationship: string;
  /** RECEPTION-OS-BUILD-V1 (Q-12=A / Q-09=D): config-gated registration
   *  fields; empty when the org config does not render them. */
  readonly relationName: string;
  readonly occupation: string;
  readonly handlingCategory: string;
  /** FD-17 (RECEPTION-FIX-AND-POLISH-BATCH-V1): the relation PREFIX pick
   *  (S/o / D/o / W/o) -- an input accelerator; the stored value stays the
   *  ONE relation_name free text (prefix + name joined at submit). */
  readonly relationPrefix: string;
  /** FD-19: optional old EHNote MR (patient_identifiers, never a column). */
  readonly externalMr: string;
  /** REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE full parity): the
   *  governed contract fields both entry points now capture through THE ONE
   *  registration field module. Config-gated at render; empty when the org
   *  contract does not name them. */
  readonly nameTitle: string;
  readonly lastName: string;
  readonly relationTitle: string;
  /** PDC-FO-GAP-CLOSURE-V1 (G2): the optional email (never requirable). */
  readonly email: string;
  readonly altMobile: string;
  readonly altMobileRelation: string;
  readonly altMobileRelationName: string;
  readonly state: string;
  readonly pincode: string;
  readonly addressArea: string;
  readonly addressDoor: string;
  readonly addressStreet: string;
  readonly addressLandmark: string;
  readonly addressTaluka: string;
  readonly addressCountry: string;
  readonly addressExpanded: boolean;
  readonly emergencyContactName: string;
  readonly emergencyContactRelation: string;
  readonly emergencyContactPhone: string;
}

export const EMPTY_ARRIVAL_FORM: ArrivalWizardForm = {
  fullName: '',
  mobile: '',
  ageMode: 'age',
  ageYears: '',
  dob: '',
  gender: '',
  city: '',
  complaint: '',
  alertFlag: false,
  consentPath: '',
  guardianName: '',
  guardianRelationship: 'guardian',
  relationName: '',
  occupation: '',
  handlingCategory: '',
  relationPrefix: '',
  externalMr: '',
  nameTitle: '',
  lastName: '',
  relationTitle: '',
  email: '',
  altMobile: '',
  altMobileRelation: '',
  altMobileRelationName: '',
  state: '',
  pincode: '',
  addressArea: '',
  addressDoor: '',
  addressStreet: '',
  addressLandmark: '',
  addressTaluka: '',
  addressCountry: '',
  addressExpanded: false,
  emergencyContactName: '',
  emergencyContactRelation: '',
  emergencyContactPhone: '',
};

/** REGISTRATION-FIELD-CONTROLS-V1: the contract's State/Country PREFILL rule
 *  ("opens PREFILLED from the config default, editable" -- REGISTRATION-
 *  CONTRACT.md section 9) applied to a wizard form. Returns the patch that
 *  fills ONLY the still-empty address defaults, so a typed or draft-restored
 *  value is never overwritten; an empty configured default seeds nothing. */
export function arrivalAddressDefaultsPatch(
  form: Pick<ArrivalWizardForm, 'state' | 'addressCountry'>,
  config: Pick<RegistrationConfigView, 'stateDefault' | 'countryDefault'>,
): Partial<ArrivalWizardForm> {
  const patch: { -readonly [K in keyof ArrivalWizardForm]?: ArrivalWizardForm[K] } = {};
  const stateDefault = (config.stateDefault ?? '').trim();
  const countryDefault = (config.countryDefault ?? '').trim();
  if (form.state.trim() === '' && stateDefault !== '') patch.state = stateDefault;
  if (form.addressCountry.trim() === '' && countryDefault !== '') {
    patch.addressCountry = countryDefault;
  }
  return patch;
}

/** A picked RETURNING patient (the search card the operator selected). The
 *  card is the Tier-0 disambiguation grade (name/UMR/age/gender/city --
 *  NEVER a phone); the mobile is typed or carried from a phone-mode search
 *  query, because the LINK still passes the server's candidacy fence. */
export interface ArrivalPickedPatient {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string;
  readonly gender: string;
  readonly ageYears: number | null;
  readonly city: string | null;
}

/** Minor derivation -- byte-identical rule to the checkpoint's isMinorForm
 *  (an EMPTY age is UNKNOWN, never a minor). */
/** F6 (AUG-12-WALK-BLOCKERS-V1): the wizard's DOB is TYPED dd/mm/yyyy (the
 *  tenant's grammar; the native date control renders the BROWSER locale's
 *  segment order -- mm/dd on a US-locale device -- never lawful here). This
 *  resolver accepts the typed dd/mm/yyyy AND a bare ISO date (restored
 *  drafts predating the change), returning the wire's YYYY-MM-DD or null.
 *  Locale-config note: a per-org date-input locale key is registered config
 *  debt; org 1 is dd/mm by market. */
export function arrivalDobIso(dobRaw: string): string | null {
  const t = dobRaw.trim();
  if (t === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  return parseDdMmYyyyToIso(t);
}

export function arrivalFormIsMinor(form: ArrivalWizardForm, nowMs: number): boolean {
  if (form.ageMode === 'age') {
    if (form.ageYears.trim() === '') return false;
    const n = Number(form.ageYears);
    return Number.isInteger(n) && n >= 0 && n < 18;
  }
  const dobIso = arrivalDobIso(form.dob);
  if (dobIso === null) return false;
  const dob = new Date(`${dobIso}T00:00:00Z`);
  if (Number.isNaN(dob.getTime())) return false;
  const now = new Date(nowMs);
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - dob.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < dob.getUTCDate())) {
    age -= 1;
  }
  return age >= 0 && age < 18;
}

// ---- D3 validation: per step, on Continue ----------------------------------

export interface ArrivalFieldError {
  /** The field token the error belongs to -- the render places the message
   *  BESIDE this field and moves focus to it (founder ruling D3). */
  readonly field: string;
  /** Plain words naming the fix -- never a code (brief 6.4 rendering 3). */
  readonly message: string;
  /** The short footer phrase ('Mobile needs 10 digits' grammar). */
  readonly footer: string;
}

function digitCount(v: string): number {
  return (v.match(/\d/g) ?? []).length;
}

/**
 * The DETAILS step's Continue validation (D3). LAW fields always validate;
 * config-required fields (city / gender) validate only when the resolved
 * set says so. Entered text survives every error (the caller never resets
 * state on a failed Continue).
 */
export function validateArrivalDetails(
  form: ArrivalWizardForm,
  required: ReadonlySet<RegistrationRequiredField>,
): readonly ArrivalFieldError[] {
  const errors: ArrivalFieldError[] = [];
  if (form.fullName.trim() === '') {
    errors.push({
      field: 'fullName',
      message: 'Full name is needed.',
      footer: 'Full name is needed',
    });
  }
  const mobile = form.mobile.trim();
  if (mobile === '') {
    errors.push({
      field: 'mobile',
      message: 'Mobile number is needed.',
      footer: 'Mobile is needed',
    });
  } else if (!isPlausibleMobile(mobile)) {
    const digits = digitCount(mobile);
    errors.push({
      field: 'mobile',
      message:
        digits < 10
          ? `Needs 10 digits. This has ${digits}.`
          : 'This does not look like a mobile number.',
      footer: 'Mobile needs 10 digits',
    });
  }
  if (form.ageMode === 'age' && form.ageYears.trim() === '') {
    errors.push({
      field: 'ageYears',
      message: 'Age or date of birth is needed.',
      footer: 'Age is needed',
    });
  }
  if (form.ageMode === 'dob' && form.dob.trim() === '') {
    errors.push({
      field: 'dob',
      message: 'Age or date of birth is needed.',
      footer: 'Date of birth is needed',
    });
  } else if (form.ageMode === 'dob' && arrivalDobIso(form.dob) === null) {
    // F6: a typed value that is not a real dd/mm/yyyy date names its fix.
    errors.push({
      field: 'dob',
      message: 'Use dd/mm/yyyy.',
      footer: 'Date needs dd/mm/yyyy',
    });
  }
  // PDC-FO-GAP-CLOSURE-V1 (G2): email is OPTIONAL (never requirable, R-A) --
  // only a TYPED junk value errors, in the desk voice; blank passes.
  if (form.email.trim() !== '' && !isPlausibleEmail(form.email)) {
    errors.push({
      field: 'email',
      message: 'This does not look like an email address.',
      footer: 'Email looks wrong',
    });
  }
  if (required.has('gender') && form.gender.trim() === '') {
    errors.push({
      field: 'gender',
      message: 'Pick a gender.',
      footer: 'Gender is needed',
    });
  }
  if (required.has('city') && form.city.trim() === '') {
    errors.push({
      field: 'city',
      message: 'City is needed.',
      footer: 'City is needed',
    });
  }
  // RECEPTION-OS-BUILD-V1 (Q-12=A): the two config-requirable registration
  // fields validate ONLY when the org's resolved set names them.
  if (required.has('relation_name') && form.relationName.trim() === '') {
    errors.push({
      field: 'relationName',
      message: 'Relation name is needed.',
      footer: 'Relation name is needed',
    });
  }
  if (required.has('occupation') && form.occupation.trim() === '') {
    errors.push({
      field: 'occupation',
      message: 'Occupation is needed.',
      footer: 'Occupation is needed',
    });
  }
  // AUG-12-WALK-BLOCKERS-V1 (D2): the Q-09=D handling TAG validates only
  // when the org's resolved set names it (vocabulary stays the sibling
  // handling_categories key -- an empty vocabulary renders no select, so a
  // row requiring the field with no categories seeded is a config fault
  // surfaced by the server's completeness check, never guessed here).
  if (required.has('handling_category') && form.handlingCategory.trim() === '') {
    errors.push({
      field: 'handlingCategory',
      message: 'Pick a handling category.',
      footer: 'Handling category is needed',
    });
  }
  return errors;
}

/** The RETURNING path's confirm validation: the record's facts are on file
 *  (confirmed, never re-typed); only the mobile is entered here because the
 *  server's candidacy fence resolves the link from it (A2-R). */
export function validateArrivalReturningConfirm(
  form: ArrivalWizardForm,
): readonly ArrivalFieldError[] {
  const mobile = form.mobile.trim();
  if (mobile === '') {
    return [
      {
        field: 'mobile',
        message: 'Mobile number is needed.',
        footer: 'Mobile is needed',
      },
    ];
  }
  if (!isPlausibleMobile(mobile)) {
    const digits = digitCount(mobile);
    return [
      {
        field: 'mobile',
        message:
          digits < 10
            ? `Needs 10 digits. This has ${digits}.`
            : 'This does not look like a mobile number.',
        footer: 'Mobile needs 10 digits',
      },
    ];
  }
  return [];
}

/** The REASON step's Continue validation (config: reason_for_visit). */
export function validateArrivalReason(
  form: ArrivalWizardForm,
  required: ReadonlySet<RegistrationRequiredField>,
): readonly ArrivalFieldError[] {
  if (required.has('reason_for_visit') && form.complaint.trim() === '') {
    return [
      {
        field: 'complaint',
        message: 'One line about the problem is needed.',
        footer: 'Reason is needed',
      },
    ];
  }
  return [];
}

/** The CONSENT step's Continue validation (NEW path only): one path chosen;
 *  a minor additionally needs the guardian block (the writer's hard rule). */
export function validateArrivalConsent(
  form: ArrivalWizardForm,
  isMinor: boolean,
): readonly ArrivalFieldError[] {
  const errors: ArrivalFieldError[] = [];
  if (isMinor && form.guardianName.trim() === '') {
    errors.push({
      field: 'guardianName',
      message: 'A guardian name is needed for a patient under 18.',
      footer: 'Guardian is needed',
    });
  }
  if (form.consentPath === '') {
    errors.push({
      field: 'consentPath',
      message: 'Choose how consent is given.',
      footer: 'Consent choice is needed',
    });
  }
  return errors;
}

/** The amber footer line above Continue (brief 6.4 rendering 2): one error
 *  names itself; several list themselves in plain words. Empty when clean. */
export function stillNeededLine(errors: readonly ArrivalFieldError[]): string {
  if (errors.length === 0) return '';
  if (errors.length === 1) return (errors[0] as ArrivalFieldError).footer;
  return `Still needed: ${errors
    .map((e) => e.footer.replace(/ is needed$| needs 10 digits$/, ''))
    .join(', ')}`;
}

/** TRUE when every step before review passes -- gates the FINAL submit only
 *  (the visible per-step Continue is never a disabled button; D3/T3). */
export function arrivalReadyToSubmit(
  form: ArrivalWizardForm,
  path: ArrivalPath,
  required: ReadonlySet<RegistrationRequiredField>,
  nowMs: number,
): boolean {
  if (path === 'returning') {
    if (validateArrivalReturningConfirm(form).length > 0) return false;
    return validateArrivalReason(form, required).length === 0;
  }
  if (validateArrivalDetails(form, required).length > 0) return false;
  if (validateArrivalReason(form, required).length > 0) return false;
  return validateArrivalConsent(form, arrivalFormIsMinor(form, nowMs)).length === 0;
}

// ---- The ONE submit body (the existing checkpoint wire, unchanged) ---------

export interface ArrivalSubmitChoice {
  readonly action: 'link' | 'create_new';
  readonly patientId?: string;
}

/**
 * Byte-compatible with the checkpoint client body the panel has always sent
 * (frontdesk-registration-checkpoint.ts CheckpointBody). The DRAFT feeds
 * THIS one builder and nothing else -- no second writer exists (R-B).
 * PAPER consent sends NO registrationPrivacyConsent (C14: not recorded
 * until photographed); DIGITAL sends it exactly as today.
 */
export function buildArrivalCheckpointBody(
  form: ArrivalWizardForm,
  nowMs: number,
  choice?: ArrivalSubmitChoice,
): Record<string, unknown> {
  const isMinor = arrivalFormIsMinor(form, nowMs);
  return {
    fullName: form.fullName.trim(),
    mobile: form.mobile.trim(),
    gender: form.gender.trim() === '' ? 'unknown' : form.gender.trim(),
    ...(form.ageMode === 'dob' && arrivalDobIso(form.dob) !== null
      ? { dob: arrivalDobIso(form.dob) as string }
      : {}),
    ...(form.ageMode === 'age' && form.ageYears.trim() !== ''
      ? { ageYears: Number(form.ageYears) }
      : {}),
    ...(form.city.trim() !== '' ? { city: form.city.trim() } : {}),
    // FD-17: the ONE stored relation value = "<prefix> <name>" joined; a
    // bare name stands alone; a prefix without a name sends nothing.
    ...(form.relationName.trim() !== ''
      ? {
          relationName:
            form.relationPrefix.trim() !== ''
              ? `${form.relationPrefix.trim()} ${form.relationName.trim()}`
              : form.relationName.trim(),
        }
      : {}),
    ...(form.occupation.trim() !== '' ? { occupation: form.occupation.trim() } : {}),
    ...(form.handlingCategory.trim() !== ''
      ? { handlingCategory: form.handlingCategory.trim() }
      : {}),
    // FD-19: the optional old MR (patient_identifiers via THE ONE writer).
    ...(form.externalMr.trim() !== '' ? { externalMr: form.externalMr.trim() } : {}),
    // REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE): the governed
    // contract fields the shared module captured -- sent to the checkpoint
    // writer (top-level camelCase, the SAME names the intake path sends), which
    // validates them through THE ONE shared parsers and stores them
    // plaintext-fenced on the lead.
    ...(form.nameTitle.trim() !== '' ? { nameTitle: form.nameTitle.trim() } : {}),
    ...(form.lastName.trim() !== '' ? { lastName: form.lastName.trim() } : {}),
    ...(form.relationTitle.trim() !== ''
      ? { relationTitle: form.relationTitle.trim() }
      : {}),
    ...(form.state.trim() !== '' ? { state: form.state.trim() } : {}),
    ...(form.pincode.trim() !== '' ? { pincode: form.pincode.trim() } : {}),
    ...(form.addressArea.trim() !== ''
      ? { addressArea: form.addressArea.trim() }
      : {}),
    ...(form.addressDoor.trim() !== ''
      ? { addressDoor: form.addressDoor.trim() }
      : {}),
    ...(form.addressStreet.trim() !== ''
      ? { addressStreet: form.addressStreet.trim() }
      : {}),
    ...(form.addressLandmark.trim() !== ''
      ? { addressLandmark: form.addressLandmark.trim() }
      : {}),
    ...(form.addressTaluka.trim() !== ''
      ? { addressTaluka: form.addressTaluka.trim() }
      : {}),
    ...(form.addressCountry.trim() !== ''
      ? { addressCountry: form.addressCountry.trim() }
      : {}),
    // PDC-FO-GAP-CLOSURE-V1 (G2): the optional email rides when given.
    ...(form.email.trim() !== '' ? { email: form.email.trim() } : {}),
    ...(form.altMobile.trim() !== '' ? { altMobile: form.altMobile.trim() } : {}),
    ...(form.altMobileRelation.trim() !== ''
      ? { altMobileRelation: form.altMobileRelation.trim() }
      : {}),
    ...(form.altMobileRelationName.trim() !== ''
      ? { altMobileRelationName: form.altMobileRelationName.trim() }
      : {}),
    ...(form.emergencyContactName.trim() !== ''
      ? { emergencyContactName: form.emergencyContactName.trim() }
      : {}),
    ...(form.emergencyContactRelation.trim() !== ''
      ? { emergencyContactRelation: form.emergencyContactRelation.trim() }
      : {}),
    ...(form.emergencyContactPhone.trim() !== ''
      ? { emergencyContactPhone: form.emergencyContactPhone.trim() }
      : {}),
    ...(form.complaint.trim() !== '' ? { complaint: form.complaint.trim() } : {}),
    ...(form.alertFlag ? { alertFlag: true } : {}),
    ...(form.consentPath === 'digital'
      ? {
          registrationPrivacyConsent: {
            grantedBy:
              isMinor && form.guardianName.trim() !== ''
                ? form.guardianName.trim()
                : form.fullName.trim() || 'patient',
            channel: 'in_person',
          },
        }
      : {}),
    ...(isMinor && form.guardianName.trim() !== ''
      ? {
          guardian: {
            name: form.guardianName.trim(),
            relationship: form.guardianRelationship,
          },
          consent: {
            grantedBy: form.guardianName.trim(),
            channel: 'in_person',
          },
        }
      : {}),
    ...(choice ? { patientChoice: choice } : {}),
  };
}

// ---- D5: the session-bound draft (a draft, never a second writer) ----------
//
// FOUNDER REVIEW CORRECTIVE (PR #414, 2026-08-01) -- DRAFT PRIVACY: the
// draft holds patient identity data, so its STORAGE LIFETIME is bound to
// the authenticated browser session, never to the device. The HOST stores
// it in session storage (dies with the browser session) AND the draft is
// KEYED TO THE AUTHENTICATED USER IDENTITY: it carries the actor (the
// staff user's own auth id -- never patient data), and parse REQUIRES the
// current session's actor to match -- a same-tab user switch makes every
// prior draft a dead draft (stale key = discarded whole). Same-tab resume
// for the SAME authenticated user keeps working (D5).

export const ARRIVAL_DRAFT_VERSION = 2;

export interface ArrivalWizardDraft {
  readonly version: number;
  readonly appointmentId: number;
  /** The AUTHENTICATED STAFF identity this draft belongs to (the auth
   *  session user id -- the operator's own, never patient data). A draft
   *  with a different or missing actor is DEAD on parse. */
  readonly actor: string;
  readonly path: ArrivalPath;
  readonly step: ArrivalStepId;
  readonly form: ArrivalWizardForm;
  readonly picked: ArrivalPickedPatient | null;
}

export function arrivalDraftKey(appointmentId: number): string {
  return `ac.arrival-draft.v${ARRIVAL_DRAFT_VERSION}.${appointmentId}`;
}

export function serializeArrivalDraft(draft: ArrivalWizardDraft): string {
  return JSON.stringify(draft);
}

/** STRICT parse: any shape mismatch discards the WHOLE draft (never a
 *  half-restored form), and the draft's actor MUST equal the CURRENT
 *  authenticated actor (non-empty) -- an authenticated-user change kills
 *  every prior draft. A discarded draft costs a redo; a corrupted or
 *  cross-user restore costs trust. */
export function parseArrivalDraft(
  raw: string | null,
  appointmentId: number,
  expectedActor: string,
): ArrivalWizardDraft | null {
  if (raw === null || raw === '') return null;
  if (typeof expectedActor !== 'string' || expectedActor === '') return null;
  try {
    const v = JSON.parse(raw) as Partial<ArrivalWizardDraft>;
    if (v === null || typeof v !== 'object') return null;
    if (v.version !== ARRIVAL_DRAFT_VERSION) return null;
    if (v.appointmentId !== appointmentId) return null;
    if (typeof v.actor !== 'string' || v.actor === '' || v.actor !== expectedActor) {
      return null;
    }
    if (v.path !== 'new' && v.path !== 'returning') return null;
    if (
      v.step !== 'search' &&
      v.step !== 'details' &&
      v.step !== 'reason' &&
      v.step !== 'consent' &&
      v.step !== 'review'
    ) {
      return null;
    }
    const f = v.form as Partial<ArrivalWizardForm> | undefined;
    if (f === null || typeof f !== 'object') return null;
    const strings: ReadonlyArray<keyof ArrivalWizardForm> = [
      'fullName',
      'mobile',
      'ageYears',
      'dob',
      'gender',
      'city',
      'complaint',
      'guardianName',
      'guardianRelationship',
    ];
    for (const k of strings) {
      if (typeof f[k] !== 'string') return null;
    }
    if (f.ageMode !== 'age' && f.ageMode !== 'dob') return null;
    if (typeof f.alertFlag !== 'boolean') return null;
    if (f.consentPath !== '' && f.consentPath !== 'digital' && f.consentPath !== 'paper') {
      return null;
    }
    let picked: ArrivalPickedPatient | null = null;
    if (v.picked !== null && v.picked !== undefined) {
      const p = v.picked as Partial<ArrivalPickedPatient>;
      if (
        typeof p.patientId !== 'string' ||
        typeof p.umr !== 'string' ||
        typeof p.fullName !== 'string' ||
        typeof p.gender !== 'string'
      ) {
        return null;
      }
      picked = {
        patientId: p.patientId,
        umr: p.umr,
        fullName: p.fullName,
        gender: p.gender,
        ageYears: typeof p.ageYears === 'number' ? p.ageYears : null,
        city: typeof p.city === 'string' ? p.city : null,
      };
    }
    if (v.path === 'returning' && picked === null) return null;
    return {
      version: ARRIVAL_DRAFT_VERSION,
      appointmentId,
      actor: v.actor,
      path: v.path,
      step: v.step,
      form: {
        // Tolerant additive base: the new contract fields default here; the
        // explicit reads below override the fields a draft carries.
        ...EMPTY_ARRIVAL_FORM,
        fullName: f.fullName as string,
        mobile: f.mobile as string,
        ageMode: f.ageMode,
        ageYears: f.ageYears as string,
        dob: f.dob as string,
        gender: f.gender as string,
        city: f.city as string,
        complaint: f.complaint as string,
        alertFlag: f.alertFlag,
        consentPath: f.consentPath as '' | RegistrationConsentPath,
        guardianName: f.guardianName as string,
        guardianRelationship: f.guardianRelationship as string,
        // RECEPTION-OS-BUILD-V1: TOLERANT additive read -- a pre-existing v2
        // draft without the new fields stays restorable (typed work survives
        // the deploy; RADHIKA), so no version bump.
        relationName: typeof f.relationName === 'string' ? f.relationName : '',
        occupation: typeof f.occupation === 'string' ? f.occupation : '',
        handlingCategory:
          typeof f.handlingCategory === 'string' ? f.handlingCategory : '',
        // RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-17/FD-19): same tolerant
        // additive posture -- older drafts restore with the fields empty.
        relationPrefix:
          typeof f.relationPrefix === 'string' ? f.relationPrefix : '',
        externalMr: typeof f.externalMr === 'string' ? f.externalMr : '',
      },
      picked,
    };
  } catch {
    return null;
  }
}

// ---- Review rows (FIELDS, NOT SENTENCES) -----------------------------------

export interface ArrivalReviewRow {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  /** The step a tap returns to (C10d in-place recovery); null = server fact. */
  readonly editStep: ArrivalStepId | null;
}

export function arrivalReviewRows(
  form: ArrivalWizardForm,
  path: ArrivalPath,
  picked: ArrivalPickedPatient | null,
  nowMs: number,
): readonly ArrivalReviewRow[] {
  const rows: ArrivalReviewRow[] = [];
  rows.push({
    key: 'name',
    label: 'Name',
    value: form.fullName.trim() || (picked ? picked.fullName : ''),
    editStep: 'details',
  });
  if (path === 'returning' && picked !== null) {
    rows.push({ key: 'id', label: 'ID', value: picked.umr, editStep: null });
  }
  rows.push({
    key: 'age',
    label: form.ageMode === 'dob' ? 'Date of birth' : 'Age',
    value: form.ageMode === 'dob' ? form.dob.trim() : form.ageYears.trim(),
    editStep: 'details',
  });
  if (form.gender.trim() !== '') {
    rows.push({
      key: 'gender',
      label: 'Gender',
      value: GENDER_DISPLAY[form.gender.trim()] ?? form.gender.trim(),
      editStep: 'details',
    });
  }
  rows.push({
    key: 'mobile',
    label: 'Mobile',
    value: form.mobile.trim(),
    editStep: 'details',
  });
  if (form.city.trim() !== '') {
    rows.push({ key: 'city', label: 'City', value: form.city.trim(), editStep: 'details' });
  }
  if (form.complaint.trim() !== '') {
    rows.push({
      key: 'reason',
      label: 'Reason',
      value: form.complaint.trim(),
      editStep: 'reason',
    });
  }
  if (form.alertFlag) {
    rows.push({ key: 'alert', label: 'Alert', value: 'On', editStep: 'reason' });
  }
  if (path === 'new' && arrivalFormIsMinor(form, nowMs) && form.guardianName.trim() !== '') {
    rows.push({
      key: 'guardian',
      label: 'Guardian',
      value: `${form.guardianName.trim()} (${
        RELATIONSHIP_DISPLAY[form.guardianRelationship] ?? form.guardianRelationship
      })`,
      editStep: 'consent',
    });
  }
  if (path === 'new') {
    rows.push({
      key: 'consent',
      label: 'Consent',
      value:
        form.consentPath === 'paper'
          ? ARRIVAL_CONSENT_VALUE_PAPER_PENDING
          : form.consentPath === 'digital'
            ? ARRIVAL_CONSENT_VALUE_DIGITAL
            : '',
      editStep: 'consent',
    });
  }
  return rows.filter((r) => r.value !== '');
}

/** The one plain sentence of consequence above the review list (must-match
 *  5 / V25): exactly what will be created or linked. */
export function arrivalConsequenceLine(
  path: ArrivalPath,
  picked: ArrivalPickedPatient | null,
): string {
  if (path === 'returning' && picked !== null) {
    return `Links this visit to ${picked.fullName} (${picked.umr}). No new record.`;
  }
  return ARRIVAL_CONSEQUENCE_NEW;
}

// ---- Display language (brief 12.3 + the polished mockups, verbatim) --------

export const ARRIVAL_TITLE_SEARCH = 'Find the patient';
export const ARRIVAL_TITLE_NEW = 'Register new patient';
export const ARRIVAL_TITLE_RETURNING = 'Confirm patient';
export const ARRIVAL_TITLE_REVIEW = 'Confirm';
export const ARRIVAL_TITLE_SUCCESS = 'Registered';

export const ARRIVAL_SEARCH_PLACEHOLDER = 'Phone, name, or ID';
export const ARRIVAL_SEARCH_BUTTON = 'Search';
export const ARRIVAL_SEARCH_TO_CONTINUE = 'Search to continue';
export const ARRIVAL_SEARCH_AGAIN = 'Search again';
export const ARRIVAL_NO_PATIENT_FOUND = 'No patient found';
export const ARRIVAL_REGISTER_NEW = 'Register new patient';
export const ARRIVAL_NONE_OF_THESE = 'None of these';
export const ARRIVAL_SELECT_CARD = 'Select';
export const ARRIVAL_SEARCH_UNAVAILABLE =
  'Search is not available right now. Register the patient - the duplicate check still runs first.';

export const ARRIVAL_REQUIRED_LEGEND = 'Required';
export const ARRIVAL_CARD_NAME_AND_AGE = 'Name and age';
export const ARRIVAL_CARD_CONTACT = 'Contact';
export const ARRIVAL_LABEL_FULL_NAME = 'Full name';
export const ARRIVAL_LABEL_AGE = 'Age';
export const ARRIVAL_LABEL_DOB = 'Date of birth';
export const ARRIVAL_LABEL_GENDER = 'Gender';
export const ARRIVAL_LABEL_MOBILE = 'Mobile';
export const ARRIVAL_LABEL_CITY = 'City';
export const ARRIVAL_MOBILE_EXAMPLE = '98765 43210';

// RECEPTION-OS-BUILD-V1 (Q-12=A / Q-09=D): the config-gated details card.
export const ARRIVAL_CARD_REGISTRATION_DETAILS = 'Registration details';
/** FD-17: the prefix moved into its own picklist; the name label stands
 *  alone (the joined "<prefix> <name>" stays the ONE stored value). */
export const ARRIVAL_LABEL_RELATION_NAME = 'Relation name';
export const ARRIVAL_LABEL_OCCUPATION = 'Occupation';
export const ARRIVAL_LABEL_HANDLING_CATEGORY = 'Patient category';
export const ARRIVAL_HANDLING_CATEGORY_NONE = 'None';
/** FD-18: the one-line plain-words hint under the category select --
 *  vocabulary stays org config; this is the display layer only. */
export const ARRIVAL_HANDLING_CATEGORY_HINT =
  'How the desk tags this patient (e.g. staff, family, camp) - for records only.';
/** FD-18: token -> plain label (display layer; the stored value stays the
 *  org's controlled token). 'staff_family' renders 'Staff family'. */
export function handlingCategoryLabel(token: string): string {
  const words = token.replace(/_/g, ' ').trim();
  return words === '' ? token : words.charAt(0).toUpperCase() + words.slice(1);
}
// FD-19: the optional external MR (old EHNote MR -> patient_identifiers).
export const ARRIVAL_LABEL_EXTERNAL_MR = 'Old MR number';
export const ARRIVAL_EXTERNAL_MR_HINT =
  'From the earlier records system, if the patient carries one. Optional.';
/** The SEAM line (never a control): Q-12=A names emergency contact
 *  mandatory, but the phone has no lawful storage owner yet
 *  (gap.patient-intake-emergency-contact-owner, the 2026-07-15 STOP) --
 *  surfaced honestly, decided by the founder, never smuggled in. */
export const ARRIVAL_EMERGENCY_CONTACT_SEAM =
  'Emergency contact - capture pending (secure phone storage under founder review).';

export const ARRIVAL_QUESTION_REASON = 'Why are they here?';
export const ARRIVAL_CARD_REASON = 'Reason';
export const ARRIVAL_ALERT_LABEL = 'Critical alert -- handle with care';

export const ARRIVAL_QUESTION_CONSENT = 'Consent';
export const ARRIVAL_MINOR_NOTE = 'Under 18 -- a guardian must consent.';
export const ARRIVAL_LABEL_GUARDIAN = 'Guardian';
export const ARRIVAL_LABEL_RELATIONSHIP = 'Relationship';
export const ARRIVAL_CONSENT_SIGN_SCREEN = 'Sign on screen';
export const ARRIVAL_CONSENT_SIGN_PAPER = 'Print and sign on paper';

export const ARRIVAL_CONSEQUENCE_NEW = 'Creates a new record.';
export const ARRIVAL_TAP_TO_EDIT = 'Tap to edit';
export const ARRIVAL_SUBMIT_LABEL = 'Register and check in';
export const ARRIVAL_CONSENT_VALUE_DIGITAL = 'Signed on screen';
export const ARRIVAL_CONSENT_VALUE_PAPER_PENDING = 'Paper - photo pending';

export const ARRIVAL_CANCEL = 'Cancel';
export const ARRIVAL_BACK = 'Back';
export const ARRIVAL_CONTINUE = 'Continue';
export const ARRIVAL_CONFIRM = 'Confirm';
export const ARRIVAL_EDIT_DETAILS = 'Edit details';
export const ARRIVAL_CANCEL_REGISTRATION = 'Cancel registration';
export const ARRIVAL_CANCEL_DIALOG_TITLE = 'Cancel registration?';
export const ARRIVAL_CANCEL_DIALOG_KEEP = 'Keep going';
export const ARRIVAL_CANCEL_DIALOG_DISCARD = 'Discard';

export const ARRIVAL_RESUMED_HEAD = 'Picked up where you left off.';
export const ARRIVAL_RESUMED_SUB = 'Nothing saved yet.';

export const ARRIVAL_SUCCESS_HEADLINE = 'Registered';
export const ARRIVAL_SUCCESS_ID_LABEL = 'ID';
export const ARRIVAL_SUCCESS_CLINIC_LABEL = 'Clinic';

// --- LEAD-UNTIL-CONSULT ruling (DOCTOR-OS-EXPERIENCE-V1, 2026-08-04) --------
// MINT RETIRED at the checkpoint: registration captures onto the LEAD; the
// patient + UMR mint at the doctor's Start Consult. The wizard's success
// grammar states that honestly, and an INCOMPLETE capture names its exact
// missing fields (the backend's closed vocabulary, labeled) and stays open.

/** Success line for the 'registration_captured' outcome: the person is
 *  registered but REMAINS AN ENQUIRY until the doctor starts the consult. */
export function capturedSuccessLine(name: string): string {
  const who = name.trim() === '' ? 'This person' : name.trim();
  return `Registration saved for ${who}. The UMR will be issued when the doctor starts the consultation.`;
}

/** Display labels for the backend's closed registration field vocabulary
 *  (mirror of REGISTRATION_FIELD_LABELS; an unknown token humanizes --
 *  honest, never dropped). */
export const REGISTRATION_MISSING_FIELD_LABELS: Readonly<
  Record<RegistrationRequiredField, string>
> = {
  full_name: 'Full name',
  mobile: 'Mobile number',
  age_or_dob: 'Age or date of birth',
  gender: 'Gender',
  city: 'City',
  handling_category: 'Handling category',
  reason_for_visit: 'Reason for visit',
  relation_name: 'Relation name',
  occupation: 'Occupation',
  // REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE): the Partha
  // contract tokens, mirroring the server REGISTRATION_FIELD_LABELS.
  name_title: 'Title',
  last_name: 'Last name',
  relation_title: 'Relation title',
  address_area: 'Area / town / village',
  state: 'State',
  pincode: 'Pincode',
  address_door: 'Door no / building',
  address_street: 'Street',
  address_landmark: 'Landmark',
  address_taluka: 'Taluka',
  address_country: 'Country',
  alt_mobile: 'Alternate phone',
  alt_mobile_relation: 'Alternate phone relation',
  alt_mobile_relation_name: 'Alternate phone relation name',
  emergency_contact_name: 'Emergency contact name',
  emergency_contact_relation: 'Emergency contact relation',
  emergency_contact_phone: 'Emergency contact phone',
};

export function missingFieldLabels(fields: readonly string[]): readonly string[] {
  return fields.map((token) => {
    const known = (
      REGISTRATION_MISSING_FIELD_LABELS as Record<string, string>
    )[token];
    if (known !== undefined) return known;
    const words = token.replace(/_/g, ' ').trim();
    return words === '' ? token : words.charAt(0).toUpperCase() + words.slice(1);
  });
}

/** The incomplete-capture banner line: saved, still needed X, Y. */
export function captureIncompleteLine(fields: readonly string[]): string {
  const labels = missingFieldLabels(fields);
  return labels.length === 0
    ? 'Registration saved but incomplete.'
    : `Registration saved but incomplete. Still needed: ${labels.join(', ')}.`;
}

export const ARRIVAL_STAGE_1_TITLE = 'Registration';
export const ARRIVAL_STAGE_1_AUTHOR = 'Reception -- done';
export const ARRIVAL_STAGE_2_TITLE = 'Patient intake';
export const ARRIVAL_STAGE_2_AUTHOR = 'Patient, on tablet';
export const ARRIVAL_STAGE_3_TITLE = 'Case sheet';
export const ARRIVAL_STAGE_3_AUTHOR = 'Doctor';

/** Dialog body: names exactly what is lost and what is not (S6). */
export function arrivalCancelDialogBody(name: string): string {
  const who = name.trim() === '' ? 'this registration' : name.trim();
  return `Details for ${who} will be discarded. The patient stays on today's board.`;
}

const GENDER_DISPLAY: Readonly<Record<string, string>> = {
  female: 'Female',
  male: 'Male',
  other: 'Other',
  unknown: 'Not stated',
};

const RELATIONSHIP_DISPLAY: Readonly<Record<string, string>> = {
  guardian: 'Guardian',
  parent: 'Parent',
  spouse: 'Spouse',
  child: 'Child',
  sibling: 'Sibling',
  other: 'Other',
};

/** The A2 gender control's options -- NOTHING pre-selected (F8). */
export const ARRIVAL_GENDER_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];

export const ARRIVAL_RELATIONSHIP_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: 'parent', label: 'Parent' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'other', label: 'Other' },
];
